<template>
  <div id="layout-wrapper">
    <NavigationBar />

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <div>
            <!-- Page header -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">HR Meetings</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item active">HR Management</li>
                      <li class="breadcrumb-item active">Meetings</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters and controls section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-3 mb-md-0">Meeting Filters</h5>
                      <div class="ms-auto">
                        <button @click="createMeeting" class="btn btn-primary me-2">
                          <i class="mdi mdi-plus me-1"></i> Schedule Meeting
                        </button>
                        <button @click="refreshData" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                        <button @click="exportMeetings" class="d-none btn btn-success">
                          <i class="mdi mdi-file-export me-1"></i> Export
                        </button>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Meeting Type</label>
                        <select v-model="filters.meetingType" class="form-select">
                          <option value="">All Meeting Types</option>
                          <option v-for="type in meetingTypes" :key="type" :value="type">
                            {{ type }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Department</label>
                        <select v-model="filters.departmentId" class="form-select">
                          <option value="">All Departments</option>
                          <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                            {{ dept.departmentName }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Date Range</label>
                        <div class="input-group">
                          <input v-model="filters.startDate" type="date" class="form-control">
                          <span class="input-group-text">to</span>
                          <input v-model="filters.endDate" type="date" class="form-control">
                        </div>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Search</label>
                        <div class="position-relative">
                          <input
                              type="text"
                              v-model="filters.search"
                              class="form-control"
                              placeholder="Search by title, organizer..."
                          >
                          <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics cards -->
            <div class="row">
              <!-- Total Meetings -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-primary h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-check font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Total Meetings</h6>
                        <h4 class="mb-0">{{ metrics.totalMeetings || 0 }}</h4>
                        <span class="text-muted font-size-12">
                          Past 30 days
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Upcoming Meetings -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-success h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-success rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-clock font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Upcoming Meetings</h6>
                        <h4 class="mb-0">{{ metrics.upcomingMeetings || 0 }}</h4>
                        <span class="text-success font-size-12">Next 7 days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- This Month -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-info h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-month font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">This Month</h6>
                        <h4 class="mb-0">{{ metrics.meetingsThisMonth || 0 }}</h4>
                        <span class="text-info font-size-12">{{ currentMonthName }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Today's Meetings -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-warning h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                            <i class="mdi mdi-calendar-today font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Today's Meetings</h6>
                        <h4 class="mb-0">{{ metrics.todayMeetings || 0 }}</h4>
                        <span class="text-warning font-size-12">{{ today }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Meeting Type Breakdown and Upcoming Meetings -->
            <div class="row">
              <!-- Meeting Type Breakdown -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Meetings by Type</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Meeting Type</th>
                            <th class="text-center">Count</th>
                            <th class="text-center">Percentage</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                            <td>{{ type }}</td>
                            <td class="text-center">{{ count }}</td>
                            <td class="text-center">
                              <div class="progress" style="height: 6px;">
                                <div
                                    class="progress-bar"
                                    :class="getMeetingTypeColor(type)"
                                    role="progressbar"
                                    :style="`width: ${getMeetingTypePercentage(type)}%`"
                                    :aria-valuenow="getMeetingTypePercentage(type)"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                              </div>
                              {{ getMeetingTypePercentage(type) }}%
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                      <p class="mt-2">No meeting type data available</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Upcoming Meetings List -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Upcoming Meetings</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="upcomingMeetings.length > 0" class="upcoming-meetings-list">
                      <div v-for="meeting in upcomingMeetings" :key="meeting.id" class="upcoming-meeting-item mb-3">
                        <div class="d-flex align-items-center p-3 border rounded">
                          <div class="meeting-date-badge me-3 text-center">
                            <span class="date-month">{{ formatDateMonth(meeting.startDate) }}</span>
                            <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(meeting.startDate) }}</span>
                          </div>
                          <div class="meeting-details flex-grow-1">
                            <h6 class="meeting-title mb-1">{{ meeting.title }}</h6>
                            <div class="meeting-meta text-muted small">
                              <span><i class="mdi mdi-clock-outline me-1"></i>{{ formatMeetingTime(meeting) }}</span>
                              <span class="ms-2"><i class="mdi mdi-map-marker me-1"></i>{{ meeting.location || 'N/A' }}</span>
                            </div>
                          </div>
                          <div class="meeting-type-badge ms-2">
                            <span class="badge rounded-pill" :class="getMeetingTypeColor(meeting.meetingType)">{{ meeting.meetingType }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-calendar font-size-48 text-muted"></i>
                      <p class="mt-2">No upcoming meetings</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Meetings list table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Meetings List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading overlay -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading meetings data...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!meetings.length" class="text-center p-5">
                      <i class="mdi mdi-calendar-remove font-size-48 text-muted"></i>
                      <h5 class="mt-2">No meetings found</h5>
                      <p class="text-muted">Try adjusting your filters or schedule a new meeting</p>
                      <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                    </div>

                    <!-- Meetings table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover table-bordered align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Meeting</th>
                          <th>Type</th>
                          <th>Date & Time</th>
                          <th>Location</th>
                          <th>Organizer</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="meeting in meetings" :key="meeting.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                                <i class="mdi mdi-calendar font-size-18 text-primary"></i>
                              </div>
                              <div>
                                <h5 class="font-size-14 mb-0">{{ meeting.title }}</h5>
                                <p class="text-muted mb-0 font-size-12">{{ meeting.meetingCode }}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span class="badge rounded-pill" :class="getMeetingTypeColor(meeting.meetingType)">{{ meeting.meetingType }}</span>
                          </td>
                          <td>
                            <div>
                              <span class="d-block"><i class="mdi mdi-calendar-range me-1"></i>{{ formatDate(meeting.startDate) }}</span>
                              <span v-if="meeting.startTime" class="d-block text-muted small">
                                  <i class="mdi mdi-clock-outline me-1"></i>{{ formatTime(meeting.startTime) }} - {{ formatTime(meeting.endTime) }}
                                </span>
                            </div>
                          </td>
                          <td>{{ meeting.location || 'N/A' }}</td>
                          <td>{{ meeting.organizer || 'N/A' }}</td>
                          <td>
                              <span class="badge" :class="getStatusBadgeClass(meeting.status)">
                                {{ meeting.status }}
                              </span>
                          </td>
                          <td class="text-center">
                            <div class="d-flex justify-content-center">
                              <button
                                  @click="viewMeetingDetails(meeting)"
                                  class="btn btn-sm btn-info me-2"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  @click="editMeeting(meeting)"
                                  class="btn btn-sm btn-primary me-2"
                                  title="Edit Meeting"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  v-if="meeting && meeting.status !== 'Canceled'"
                                  @click="cancelMeeting(meeting)"
                                  class="btn btn-sm btn-danger"
                                  title="Cancel Meeting">
                                <i class="mdi mdi-close"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="meetings.length" class="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        Showing <span class="fw-medium">{{ meetings.length }}</span> of
                        <span class="fw-medium">{{ pagination.totalMeetings }}</span> meetings
                      </div>
                      <nav v-if="pagination.totalPages > 1">
                        <ul class="pagination pagination-rounded mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                              <i class="mdi mdi-chevron-left"></i>
                            </a>
                          </li>
                          <li
                              v-for="page in getPageNumbers()"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                          >
                            <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                              {{ page }}
                            </a>
                            <span v-else class="page-link">{{ page }}</span>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                              <i class="mdi mdi-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Create/Edit Modal -->
  <div class="modal fade" id="meetingModal" tabindex="-1" aria-labelledby="meetingModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingModalLabel">{{ isEditMode ? 'Edit Meeting' : 'Schedule New Meeting' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitMeeting">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label">Meeting Title <span class="text-danger">*</span></label>
                <input v-model="meetingForm.title" type="text" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Meeting Type <span class="text-danger">*</span></label>
                <select v-model="meetingForm.meetingType" class="form-select" required>
                  <option value="" disabled>Select Meeting Type</option>
                  <option v-for="type in meetingTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Department</label>
                <select v-model="meetingForm.departmentId" class="form-select">
                  <option value="">All Departments</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.departmentName }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Date <span class="text-danger">*</span></label>
                <input v-model="meetingForm.startDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
                <input v-model="meetingForm.duration" type="number" min="15" step="15" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Start Time <span class="text-danger">*</span></label>
                <input v-model="meetingForm.startTime" type="time" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">End Time</label>
                <input v-model="meetingForm.endTime" type="time" class="form-control" disabled>
              </div>

              <div class="col-md-12">
                <label class="form-label">Location</label>
                <input v-model="meetingForm.location" type="text" class="form-control">
              </div>

              <div class="col-md-12">
                <label class="form-label">Virtual Meeting Link</label>
                <input v-model="meetingForm.virtualMeetingLink" type="text" class="form-control">
              </div>

              <div class="col-md-6">
                <label class="form-label">Priority</label>
                <select v-model="meetingForm.priority" class="form-select">
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Status</label>
                <select v-model="meetingForm.status" class="form-select">
                  <option value="Scheduled">Scheduled</option>
                  <option value="Postponed">Postponed</option>
                  <option value="Canceled">Canceled</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div class="col-md-12">
                <label class="form-label">Agenda</label>
                <textarea v-model="meetingForm.agenda" class="form-control" rows="3"></textarea>
              </div>

              <div class="col-md-12">
                <label class="form-label">Participants</label>
                <select v-model="meetingForm.participants" class="form-select" multiple>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.firstName }} {{ employee.lastName }} ({{ employee.departmentName }})
                  </option>
                </select>
                <small class="text-muted">Hold Ctrl/Cmd to select multiple employees</small>
              </div>

              <div class="col-md-12">
                <label class="form-label">Attachments</label>
                <input type="file" class="form-control" multiple>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitMeeting">
            {{ isEditMode ? 'Update Meeting' : 'Schedule Meeting' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Details Modal -->
  <div class="modal fade" id="meetingDetailsModal" tabindex="-1" aria-labelledby="meetingDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingDetailsModalLabel">Meeting Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedMeeting">
            <div class="row mb-4">
              <div class="col-md-8">
                <h4 class="font-size-16">{{ selectedMeeting.title }}</h4>
                <p class="text-muted mb-1">
                  <span class="badge rounded-pill" :class="getMeetingTypeColor(selectedMeeting.meetingType)">
                    {{ selectedMeeting.meetingType }}
                  </span>
                </p>
                <p class="text-muted mb-0">Meeting Code: {{ selectedMeeting.meetingCode }}</p>
              </div>
              <div class="col-md-4 text-md-end">
                <span class="badge" :class="getStatusBadgeClass(selectedMeeting.status)">
                  {{ selectedMeeting.status }}
                </span>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Meeting Information</h6>
                  </div>
                  <div class="card-body">
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-calendar me-2"></i>Date:</span>
                          <span class="text-muted">{{ formatDate(selectedMeeting.startDate) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item" v-if="selectedMeeting.startTime">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-clock-outline me-2"></i>Time:</span>
                          <span class="text-muted">{{ formatTime(selectedMeeting.startTime) }} - {{ formatTime(selectedMeeting.endTime) }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-map-marker me-2"></i>Location:</span>
                          <span class="text-muted">{{ selectedMeeting.location || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item" v-if="selectedMeeting.virtualMeetingLink">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-video me-2"></i>Virtual Link:</span>
                          <a :href="selectedMeeting.virtualMeetingLink" target="_blank">Join Meeting</a>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-account me-2"></i>Organizer:</span>
                          <span class="text-muted">{{ selectedMeeting.organizer || 'N/A' }}</span>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="d-flex justify-content-between">
                          <span><i class="mdi mdi-flag me-2"></i>Priority:</span>
                          <span class="text-muted">{{ selectedMeeting.priority }}</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border h-100">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Agenda</h6>
                  </div>
                  <div class="card-body">
                    <p v-if="selectedMeeting.agenda">{{ selectedMeeting.agenda }}</p>
                    <p v-else class="text-muted text-center">No agenda provided</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Participants</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="selectedMeeting.participantsList && selectedMeeting.participantsList.length > 0">
                      <div class="table-responsive">
                        <table class="table table-sm table-striped">
                          <thead>
                          <tr>
                            <th>Name</th>
                            <th>Department</th>
                            <th>Response</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(participant, index) in selectedMeeting.participantsList" :key="index">
                            <td>{{ participant.name }}</td>
                            <td>{{ participant.department || 'N/A' }}</td>
                            <td>
                                  <span class="badge" :class="getResponseBadgeClass(participant.response)">
                                    {{ participant.response || 'Pending' }}
                                  </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <p v-else class="text-muted text-center">No participants added</p>
                  </div>
                </div>
              </div>
            </div>


            <!-- Meeting Minutes Section -->
            <div class="row mt-4" v-if="selectedMeeting && selectedMeeting.minutes">
              <div class="col-md-12">
                <div class="card border">
                  <div class="card-header bg-light d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Meeting Minutes</h6>
                    <span class="text-muted small" v-if="selectedMeeting.minutes.createdAt">
          <i class="mdi mdi-clock-outline me-1"></i>{{ formatDateTime(selectedMeeting.minutes.createdAt) }}
        </span>
                  </div>
                  <div class="card-body">
                    <!-- Minutes Content -->
                    <div class="mb-4">
                      <p class="mb-0 text-pre-wrap">{{ selectedMeeting.minutes.content }}</p>
                    </div>

                    <!-- Action Items -->
                    <div v-if="selectedMeeting.minutes.actionItems && selectedMeeting.minutes.actionItems.length > 0" class="mt-4">
                      <h6 class="card-subtitle mb-3 text-muted">Action Items</h6>
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Action Item</th>
                            <th>Assigned To</th>
                            <th>Due Date</th>
                            <th>Status</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(item, index) in selectedMeeting.minutes.actionItems" :key="index">
                            <td>{{ item.description }}</td>
                            <td>{{ item.assignedTo }}</td>
                            <td>{{ formatDate(item.dueDate) }}</td>
                            <td>
                  <span class="badge" :class="getActionItemStatusClass(item.status || 'Pending')">
                    {{ item.status || 'Pending' }}
                  </span>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>




          </div>

          <!-- Meeting Actions -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="d-flex justify-content-end">



                <!-- Add this button to the Meeting Actions section in the meeting details modal -->
                <button
                    v-if="selectedMeeting && (selectedMeeting.status === 'Completed' || selectedMeeting.status === 'Scheduled')"
                    @click="openMeetingMinutesModal(selectedMeeting)"
                    class="btn btn-info me-2">
                  <i class="mdi mdi-file-document-edit me-1"></i> {{ selectedMeeting.minutes ? 'Edit Minutes' : 'Add Minutes' }}
                </button>

                <!-- Status actions based on current status -->
                <button
                    v-if="selectedMeeting && selectedMeeting.status === 'Scheduled'"
                    @click="completeMeeting(selectedMeeting)"
                    class="btn btn-success me-2">
                  <i class="mdi mdi-check-circle me-1"></i> Mark as Completed
                </button>
                <button
                    v-if="selectedMeeting && selectedMeeting.status !== 'Canceled'"
                    @click="cancelMeeting(selectedMeeting)"
                    class="btn btn-danger me-2">
                  <i class="mdi mdi-close-circle me-1"></i> Cancel Meeting
                </button>

                <button
                    @click="editMeeting(selectedMeeting)"
                    class="btn btn-primary me-2">
                  <i class="mdi mdi-pencil me-1"></i> Edit
                </button>
                <button
                    @click="exportMeetingDetails(selectedMeeting)"
                    class="btn btn-info">
                  <i class="mdi mdi-download me-1"></i> Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>


  <!-- Cancel Meeting Modal -->
  <div class="modal fade" id="cancelMeetingModal" tabindex="-1" aria-labelledby="cancelMeetingModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="cancelMeetingModalLabel">Cancel Meeting</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to cancel this meeting?</p>
          <div class="mb-3">
            <label for="cancellationReason" class="form-label">Reason for cancellation</label>
            <textarea
                id="cancellationReason"
                v-model="cancellationReason"
                class="form-control"
                rows="3"
                placeholder="Please provide a reason for cancellation..."></textarea>
          </div>
          <div class="form-check mb-3">
            <input
                class="form-check-input"
                type="checkbox"
                v-model="notifyParticipants"
                id="notifyParticipantsCheck">
            <label class="form-check-label" for="notifyParticipantsCheck">
              Notify all participants about cancellation
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-danger" @click="confirmCancelMeeting">
            <i class="mdi mdi-close-circle me-1"></i> Cancel Meeting
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Minutes Modal -->
  <div class="modal fade" id="meetingMinutesModal" tabindex="-1" aria-labelledby="meetingMinutesModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="meetingMinutesModalLabel">Add Meeting Minutes</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitMeetingMinutes">
            <div class="mb-3">
              <label class="form-label">Meeting Minutes <span class="text-danger">*</span></label>
              <textarea v-model="minutesForm.content" class="form-control" rows="8" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Action Items</label>
              <div v-for="(item, index) in minutesForm.actionItems" :key="index" class="d-flex mb-2">
                <input v-model="item.description" type="text" class="form-control me-2" placeholder="Action item description">
                <input v-model="item.assignedTo" type="text" class="form-control me-2" placeholder="Assigned to">
                <input v-model="item.dueDate" type="date" class="form-control me-2">
                <button type="button" class="btn btn-danger btn-sm" @click="removeActionItem(index)">
                  <i class="mdi mdi-delete"></i>
                </button>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary mt-2" @click="addActionItem">
                <i class="mdi mdi-plus me-1"></i> Add Action Item
              </button>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" v-model="minutesForm.notifyParticipants" id="notifyParticipantsMinutes">
              <label class="form-check-label" for="notifyParticipantsMinutes">
                Send meeting minutes to all participants
              </label>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitMeetingMinutes">
            <i class="mdi mdi-content-save me-1"></i> Save Minutes
          </button>
        </div>
      </div>
    </div>
  </div>


</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "HRMeetings",
  components: {NavigationBar},
  data() {
    return {
      loading: false,
      meetings: [],
      upcomingMeetings: [],
      departments: [],
      employees: [],
      meetingTypes: ['Team Meeting', 'One-on-One', 'Board Meeting', 'Interview', 'Conference Call', 'Training', 'Workshop', 'Brainstorming', 'Review', 'Other'],
      filters: {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30), // Last 30 days
        endDate: this.getDateOffset(30), // Next 30 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalMeetings: 0,
        limit: 10
      },
      metrics: {
        totalMeetings: 0,
        upcomingMeetings: 0,
        meetingsThisMonth: 0,
        todayMeetings: 0,
        typeCounts: {}
      },
      selectedMeeting: null,
      meetingForm: {
        title: "",
        agenda: "",
        meetingType: "",
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        duration: 60,
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        participants: [],
        priority: "Medium",
        status: "Scheduled"
      },
      isEditMode: false,
      cancellationReason: "",
      notifyParticipants: true,
      minutesForm: {
        content: "",
        actionItems: [],
        notifyParticipants: true
      },
    };
  },
  computed: {
    today() {
      return new Date().toLocaleDateString();
    },
    currentMonthName() {
      return new Date().toLocaleString('default', { month: 'long' });
    }
  },
  mounted() {
    this.getDepartments();
    this.getEmployees();
    this.getMeetings();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getMeetings() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getMeetings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.meetings = res.data.data.meetings || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalMeetings: 0,
                limit: 10
              };

              // Get upcoming meetings (next 7 days)
              const today = new Date();
              const nextWeek = new Date();
              nextWeek.setDate(today.getDate() + 7);

              this.upcomingMeetings = this.meetings
                  .filter(meeting => {
                    const meetingDate = new Date(meeting.startDate);
                    return meetingDate >= today && meetingDate <= nextWeek && meeting.status !== 'Canceled';
                  })
                  .slice(0, 5); // Top 5 upcoming meetings
            } else {
              console.error("Error fetching meetings:", res.data.message);
              this.showToast("error", "Failed to load meetings data: " + res.data.message);
              this.meetings = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meetings");
            this.meetings = [];
            this.metrics = {};
          });
    },


    refreshData() {
      this.getMeetings();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        meetingType: "",
        startDate: this.getDateOffset(-30),
        endDate: this.getDateOffset(30),
        search: "",
        page: 1,
        limit: 10
      };
      this.getMeetings();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getMeetings();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createMeeting() {
      this.isEditMode = false;
      this.meetingForm = {
        title: "",
        agenda: "",
        meetingType: "",
        startDate: new Date().toISOString().split('T')[0], // Today's date
        duration: 60, // Default to 60 minutes
        startTime: "",
        endTime: "",
        location: "",
        virtualMeetingLink: "",
        departmentId: "",
        participants: [],
        priority: "Medium",
        status: "Scheduled"
      };
      new bootstrap.Modal(document.getElementById('meetingModal')).show();
    },

    editMeeting(meeting) {
      this.isEditMode = true;
      this.selectedMeeting = meeting;

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Calculate duration based on start and end time
      let duration = 60; // Default
      if (meeting.startTime && meeting.endTime) {
        const start = new Date(`2000-01-01T${meeting.startTime}`);
        const end = new Date(`2000-01-01T${meeting.endTime}`);
        duration = (end - start) / 60000; // Convert milliseconds to minutes
      }

      // Prepare form data
      this.meetingForm = {
        id: meeting.id,
        title: meeting.title,
        agenda: meeting.agenda || "",
        meetingType: meeting.meetingType,
        startDate: meeting.startDate,
        duration: duration,
        startTime: meeting.startTime || "",
        endTime: meeting.endTime || "",
        location: meeting.location || "",
        virtualMeetingLink: meeting.virtualMeetingLink || "",
        departmentId: meeting.departmentId || "",
        participants: meeting.participantsList ? meeting.participantsList.map(att => att.employeeId) : [],
        priority: meeting.priority || "Medium",
        status: meeting.status || "Scheduled"
      };

      // Open the meeting modal
      new bootstrap.Modal(document.getElementById('meetingModal')).show();
    },

    // Calculate end time when start time or duration changes
    updateEndTime() {
      if (this.meetingForm.startTime && this.meetingForm.duration) {
        const [hours, minutes] = this.meetingForm.startTime.split(':').map(Number);
        const startDate = new Date();
        startDate.setHours(hours, minutes, 0);

        const endDate = new Date(startDate.getTime() + this.meetingForm.duration * 60000);
        const endHours = endDate.getHours().toString().padStart(2, '0');
        const endMinutes = endDate.getMinutes().toString().padStart(2, '0');

        this.meetingForm.endTime = `${endHours}:${endMinutes}`;
      } else {
        this.meetingForm.endTime = '';
      }
    },

    submitMeeting() {
      // Validate required fields
      if (!this.meetingForm.title || !this.meetingForm.startDate || !this.meetingForm.meetingType || !this.meetingForm.startTime) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Update end time based on start time and duration
      this.updateEndTime();

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateMeeting");
        data.append("meetingId", this.meetingForm.id);
      } else {
        data.append("function", "createMeeting");
      }

      // Add all form fields
      data.append("title", this.meetingForm.title);
      data.append("agenda", this.meetingForm.agenda);
      data.append("meetingType", this.meetingForm.meetingType);
      data.append("startDate", this.meetingForm.startDate);
      data.append("startTime", this.meetingForm.startTime);
      data.append("endTime", this.meetingForm.endTime);
      data.append("location", this.meetingForm.location);
      data.append("virtualMeetingLink", this.meetingForm.virtualMeetingLink);
      data.append("departmentId", this.meetingForm.departmentId);
      data.append("priority", this.meetingForm.priority);
      data.append("status", this.meetingForm.status);

      // Handle participants
      if (this.meetingForm.participants.length > 0) {
        // Create participants array of objects
        const participantsArray = this.meetingForm.participants.map(empId => {
          const employee = this.employees.find(emp => emp.id === empId);
          return {
            employeeId: empId,
            name: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown'
          };
        });
        data.append("participants", JSON.stringify(participantsArray));
      }

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Meeting updated successfully" : "Meeting created successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('meetingModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error saving meeting:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} meeting: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} meeting`);
          });
    },

    viewMeetingDetails(meeting) {
      this.selectedMeeting = null;
      this.getMeetingDetails(meeting.id);
    },

    cancelMeeting(meeting) {
      this.selectedMeeting = meeting;
      this.cancellationReason = "";
      this.notifyParticipants = true;

      // Close any other open modals
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the cancel meeting modal
      new bootstrap.Modal(document.getElementById('cancelMeetingModal')).show();
    },

    confirmCancelMeeting() {
      if (!this.selectedMeeting) return;

      const data = new FormData();
      data.append("function", "cancelMeeting");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("reason", this.cancellationReason);
      data.append("notifyParticipants", this.notifyParticipants ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting canceled successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('cancelMeetingModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error canceling meeting:", res.data.message);
              this.showToast("error", "Failed to cancel meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when canceling meeting");
          });
    },

    completeMeeting(meeting) {
      const data = new FormData();
      data.append("function", "completeMeeting");
      data.append("meetingId", meeting.id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting marked as completed");

              // Close the details modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh the meetings data
              this.getMeetings();
            } else {
              console.error("Error completing meeting:", res.data.message);
              this.showToast("error", "Failed to complete meeting: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when completing meeting");
          });
    },

    exportMeetings() {
      const data = new FormData();
      data.append("function", "exportMeetingsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.meetingType) {
        data.append("meetingType", this.filters.meetingType);
      }

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    exportMeetingDetails(meeting) {
      if (!meeting) return;

      const data = new FormData();
      data.append("function", "exportMeetingDetails");
      data.append("meetingId", meeting.id);

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    addActionItem() {
      this.minutesForm.actionItems.push({
        description: "",
        assignedTo: "",
        dueDate: ""
      });
    },

    removeActionItem(index) {
      this.minutesForm.actionItems.splice(index, 1);
    },
    openMeetingMinutesModal(meeting) {
      this.selectedMeeting = meeting;

      // Reset the minutes form
      this.minutesForm = {
        content: "",
        actionItems: [],
        notifyParticipants: true
      };

      // Check if there are existing minutes to load them
      if (meeting.minutes) {
        this.minutesForm.content = meeting.minutes.content || "";
        this.minutesForm.actionItems = meeting.minutes.actionItems || [];
      }

      // Close details modal if open
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Open the minutes modal
      new bootstrap.Modal(document.getElementById('meetingMinutesModal')).show();
    },

    getMeetingDetails(meetingId) {
      const data = new FormData();
      data.append("function", "getMeetingDetails");
      data.append("meetingId", meetingId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedMeeting = res.data.data.selectedMeeting || res.data.data.meetingDetails;

              // Process meeting notes if available (convert from JSON string if needed)
              if (this.selectedMeeting && this.selectedMeeting.meetingNotes) {
                try {
                  // If it's a JSON string, parse it
                  if (typeof this.selectedMeeting.meetingNotes === 'string') {
                    this.selectedMeeting.minutes = JSON.parse(this.selectedMeeting.meetingNotes);
                  } else {
                    // It's already an object
                    this.selectedMeeting.minutes = this.selectedMeeting.meetingNotes;
                  }

                  // Ensure proper structure - initialize with defaults if properties are missing
                  this.selectedMeeting.minutes = {
                    content: this.selectedMeeting.minutes.content || '',
                    createdAt: this.selectedMeeting.minutes.addedAt || this.selectedMeeting.updatedAt || new Date().toISOString(),
                    actionItems: this.selectedMeeting.minutes.actionItems || []
                  };

                  // Format action item dates if present
                  if (this.selectedMeeting.minutes.actionItems && this.selectedMeeting.minutes.actionItems.length > 0) {
                    this.selectedMeeting.minutes.actionItems.forEach(item => {
                      // Check for overdue items and update status
                      if (item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'Completed') {
                        item.status = 'Overdue';
                      }
                    });
                  }
                } catch (error) {
                  console.error("Error parsing meeting notes:", error);
                  // Initialize with an empty structure to prevent null reference errors
                  this.selectedMeeting.minutes = {
                    content: '',
                    createdAt: new Date().toISOString(),
                    actionItems: []
                  };
                }
              } else {
                // Initialize with an empty structure to prevent null reference errors
                this.selectedMeeting.minutes = {
                  content: '',
                  createdAt: new Date().toISOString(),
                  actionItems: []
                };
              }

              // Open the meeting details modal
              new bootstrap.Modal(document.getElementById('meetingDetailsModal')).show();
            } else {
              console.error("Error fetching meeting details:", res.data.message);
              this.showToast("error", "Failed to load meeting details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching meeting details");
          });
    },


    submitMeetingMinutes() {
      // Validate required fields
      if (!this.minutesForm.content) {
        this.showToast("error", "Please enter meeting minutes content");
        return;
      }

      const data = new FormData();
      data.append("function", "saveMeetingMinutes");
      data.append("meetingId", this.selectedMeeting.id);
      data.append("content", this.minutesForm.content);
      data.append("notifyParticipants", this.minutesForm.notifyParticipants ? 1 : 0);

      // Create the meeting notes object in the expected format
      const meetingNotes = {
        content: this.minutesForm.content,
        actionItems: this.minutesForm.actionItems || [],
        addedBy: this.selectedMeeting.organizer || "USER", // Use meeting organizer or default
        addedAt: new Date().toISOString().replace('T', ' ').substring(0, 19) // Format: YYYY-MM-DD HH:MM:SS
      };

      data.append("meetingNotes", JSON.stringify(meetingNotes));

      if (this.minutesForm.actionItems.length > 0) {
        data.append("actionItems", JSON.stringify(this.minutesForm.actionItems));
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Meeting minutes saved successfully");

              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('meetingMinutesModal'));
              if (modal) {
                modal.hide();
              }

              // Refresh meeting details
              this.getMeetingDetails(this.selectedMeeting.id);
            } else {
              console.error("Error saving meeting minutes:", res.data.message);
              this.showToast("error", "Failed to save meeting minutes: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when saving meeting minutes");
          });
    },

    // Helper methods
    viewMeetingMinutes(meeting) {
      this.selectedMeeting = meeting;

      // You could either show a dedicated modal for viewing minutes
      // or just scroll to the minutes section in the details modal
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('meetingDetailsModal'));
      if (!detailsModal) {
        this.viewMeetingDetails(meeting);
      }
    },

    getActionItemStatusClass(status) {
      const statusMap = {
        'Completed': 'bg-success',
        'In Progress': 'bg-warning',
        'Pending': 'bg-secondary',
        'Overdue': 'bg-danger',
        'Not Started': 'bg-info'
      };

      return statusMap[status] || 'bg-secondary';
    },

    formatTime(timeString) {
      if (!timeString) return '';

      // If the time string is already in 12-hour format
      if (timeString.includes('AM') || timeString.includes('PM')) {
        return timeString;
      }

      // Convert 24-hour format to 12-hour format
      const [hours, minutes] = timeString.split(':');
      let hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12;
      hour = hour ? hour : 12; // the hour '0' should be '12'
      return `${hour}:${minutes} ${ampm}`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatMeetingTime(meeting) {
      if (!meeting.startTime) return 'No Time Specified';

      return `${this.formatTime(meeting.startTime)}${meeting.endTime ? ' - ' + this.formatTime(meeting.endTime) : ''}`;
    },

    getMeetingTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'Team Meeting': 'bg-success',
        'One-on-One': 'bg-info',
        'Board Meeting': 'bg-primary',
        'Interview': 'bg-warning',
        'Conference Call': 'bg-purple',
        'Training': 'bg-indigo',
        'Workshop': 'bg-teal',
        'Brainstorming': 'bg-pink',
        'Review': 'bg-orange',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Scheduled': 'bg-success',
        'Canceled': 'bg-danger',
        'Completed': 'bg-info',
        'In Progress': 'bg-warning',
        'Postponed': 'bg-warning'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'bg-secondary';

      const responseMap = {
        'Accepted': 'bg-success',
        'Declined': 'bg-danger',
        'Tentative': 'bg-warning',
        'Pending': 'bg-secondary'
      };

      return responseMap[response] || 'bg-secondary';
    },

    getMeetingTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalMeetings) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalMeetings) * 100).toFixed(1);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.meetingType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.startDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.endDate': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getMeetings();
    }, 500),

    'meetingForm.startTime': function() {
      this.updateEndTime();
    },

    'meetingForm.duration': function() {
      this.updateEndTime();
    }
  }
};
</script>

<style scoped>
/* Add these styles to your <style> section */

/* For preserving whitespace in meeting minutes */
.text-pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

/* Style for the meeting minutes card */
.meeting-minutes-card {
  border-left: 4px solid #17a2b8 !important;
}

/* Style for action items table */
.action-item-row {
  transition: background-color 0.2s ease;
}

.action-item-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Add icons for action item status */
.action-item-completed::before {
  content: "✓";
  margin-right: 5px;
  color: #28a745;
}

.action-item-pending::before {
  content: "⟳";
  margin-right: 5px;
  color: #6c757d;
}

.action-item-overdue::before {
  content: "!";
  margin-right: 5px;
  color: #dc3545;
}

.meeting-date-badge {
  min-width: 60px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

.border-left-primary {
  border-left: 4px solid #4e73df !important;
}

.border-left-success {
  border-left: 4px solid #1cc88a !important;
}

.border-left-info {
  border-left: 4px solid #36b9cc !important;
}

.border-left-warning {
  border-left: 4px solid #f6c23e !important;
}

.bg-purple {
  background-color: #6f42c1 !important;
  color: #fff;
}

.bg-indigo {
  background-color: #6610f2 !important;
  color: #fff;
}

.bg-teal {
  background-color: #20c997 !important;
  color: #fff;
}

.bg-pink {
  background-color: #e83e8c !important;
  color: #fff;
}

.bg-orange {
  background-color: #fd7e14 !important;
  color: #fff;
}

/* Add some responsive spacing */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .upcoming-meeting-item {
    flex-direction: column;
  }

  .meeting-date-badge {
    margin-bottom: 0.75rem;
  }
}
</style>